/**
 * YT Text Accent — auto-wrap [bracket] text in headings + rich-text containers
 *
 * Lets merchants put accent words in square brackets in any heading or
 * rich-text block and have them render in the section's accent font:
 *
 *   Input:  "Live [uncaged] since [2008]"
 *   Render: Live <span class="yt-text-accent">uncaged</span> since <span ...>2008</span>
 *
 * The wrapper class `yt-text-accent` resolves its font via the CSS variable
 * `--yt-accent-font`, which YT sections (yt-grid, yt-media-columns, yt-card-
 * carousel) set on their section wrapper based on the `accent_font` setting.
 * Fallback is Knewave so legacy sections without the variable still render.
 *
 * Why JS and not Liquid:
 * Impact's `blocks/heading.liquid` renders text via `snippets/styled-text.liquid`
 * which applies `{{ content | strip | escape }}` — any HTML we'd inject would be
 * encoded back to text. Both files are Impact core (CLAUDE.md §1: never modify).
 *
 * Scope: headings + rich-text containers only. Body copy, button labels, and
 * form inputs are NOT processed.
 */
(function () {
  'use strict';

  const SELECTORS = [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    '.h0', '.h1', '.h2', '.h3', '.h4', '.h5', '.h6',
    '.heading', '.prose'
  ].join(',');
  const PROCESSED = 'data-yt-text-accent-processed';
  const ACCENT_CLASS = 'yt-text-accent';
  const BRACKET_RE = /\[([^\]]+)\]/g;

  function wrapBrackets(textNode) {
    const text = textNode.nodeValue;
    BRACKET_RE.lastIndex = 0;
    if (!BRACKET_RE.test(text)) return;
    BRACKET_RE.lastIndex = 0;

    const frag = document.createDocumentFragment();
    let last = 0;
    let m;
    while ((m = BRACKET_RE.exec(text)) !== null) {
      if (m.index > last) {
        frag.appendChild(document.createTextNode(text.slice(last, m.index)));
      }
      const span = document.createElement('span');
      span.className = ACCENT_CLASS;
      span.textContent = m[1];
      frag.appendChild(span);
      last = BRACKET_RE.lastIndex;
    }
    if (last < text.length) {
      frag.appendChild(document.createTextNode(text.slice(last)));
    }
    textNode.parentNode.replaceChild(frag, textNode);
  }

  function processElement(el) {
    if (el.hasAttribute(PROCESSED)) return;
    el.setAttribute(PROCESSED, '');

    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        // Skip text already inside an accent span (avoid re-wrapping)
        let p = node.parentElement;
        while (p && p !== el) {
          if (p.classList && p.classList.contains(ACCENT_CLASS)) {
            return NodeFilter.FILTER_REJECT;
          }
          p = p.parentElement;
        }
        return /\[[^\]]+\]/.test(node.nodeValue)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      }
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(wrapBrackets);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(SELECTORS)) processElement(root);
    root.querySelectorAll(SELECTORS).forEach(processElement);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function onReady() {
      scan(document.body);
    }, { once: true });
  } else {
    scan(document.body);
  }

  // Re-scan after Theme Editor re-renders a section
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})();
